import { useNavigate } from 'react-router-dom';
import { t } from '@aquabox/shared';
import { Icon } from '../../components/Icon';
import type { IconName } from '../../components/Icon';
import { useTrips } from '../../api/queries';
import type { Trip } from '../../api/types';

/**
 * "Kapanış durumu" kartı (panel §3.3 C): bugünün seferleri, denklem/ilerleme durumuna göre
 * renklenir. Bizim seferimiz yoksa ince metin + "/seferler" bağlantısı gösterilir.
 *
 * Panel araçlarının durumu (Hayat/Abant) buraya GİRMEZ — onların işi kendi panel ekranı
 * (kullanıcı kararı 30.09.2026).
 */
export function ClosingStatusCard() {
  const navigate = useNavigate();
  const trips = useTrips({ dateFilter: 'today', pageSize: '20' });
  const rows = trips.data?.items ?? [];
  return (
    <div className="card card-pad">
      <h2 className="section-title">{t('panel.closingStatus')}</h2>
      <p className="muted-line" style={{ margin: '4px 0 10px' }}>
        {t('panel.todaysTrips')}
      </p>
      {rows.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '10px 0',
          }}
        >
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {t('panel.noOurTrips')}
          </span>
          <button
            type="button"
            onClick={() => void navigate('/seferler')}
            style={{
              alignSelf: 'flex-start',
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--primary)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('panel.goToTrips')} →
          </button>
        </div>
      ) : (
        <ul style={{ listStyle: 'none' }}>
          {rows.map((trip) => {
            const info = tripClosingInfo(trip);
            return (
              <li
                key={trip.id}
                onClick={
                  info.clickable ? () => void navigate(`/seferler?trip=${trip.id}`) : undefined
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  height: 52,
                  borderTop: '1px solid var(--border)',
                  borderLeft: info.critical ? '3px solid var(--danger)' : '3px solid transparent',
                  background: info.critical ? 'var(--bg-danger-soft)' : undefined,
                  paddingLeft: 9,
                  cursor: info.clickable ? 'pointer' : 'default',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 14, width: 104 }}>
                  {trip.vehicle.plate}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 13.5,
                    color: 'var(--text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {trip.driver.user.fullName}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    color: info.color,
                    flexShrink: 0,
                  }}
                >
                  <Icon name={info.icon} size={15} />
                  {info.text}
                </span>
                {info.clickable ? (
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: 'var(--primary)',
                      flexShrink: 0,
                    }}
                  >
                    {t('map.goToTrip')} →
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Sefer başına kapanış durumu satırı — durum, denklem ve teslimat sayısından türer (gerçek veri). */
function tripClosingInfo(trip: Trip): {
  text: string;
  color: string;
  icon: IconName;
  critical: boolean;
  clickable: boolean;
} {
  if (trip.status === 'CLOSED') {
    if (trip.equation.applicable && trip.equation.hasVariance) {
      return {
        text: t('panel.jugsOpen', { n: trip.equation.openCount }),
        color: 'var(--danger)',
        icon: 'x',
        critical: true,
        clickable: true,
      };
    }
    return {
      text: t('panel.equationMatched'),
      color: 'var(--success)',
      icon: 'check',
      critical: false,
      clickable: false,
    };
  }
  if (trip.status === 'ON_ROUTE') {
    return {
      text: t('panel.onRouteDeliveries', { n: trip.deliveryCount }),
      color: 'var(--warning)',
      icon: 'van',
      critical: false,
      clickable: false,
    };
  }
  return {
    text: t('panel.awaitingClose'),
    color: 'var(--text-secondary)',
    icon: 'clock',
    critical: false,
    clickable: false,
  };
}
